import { Injectable } from "@angular/core"
import { Observable, BehaviorSubject, map } from "rxjs"
import { StationStoreService } from "./station-store.service"
import { environment } from "../../environments/environment"
import { ApiService } from "./api.service"
import { Station } from "../models/station.model"
import { formatRateDayRuns } from "../utils/rate-days"

export interface StationRate {
  id: number
  station_id: number
  station_call_sign?: string
  station_name?: string
  friendly_name: string
  spot_length: 15 | 30 | 60
  start_hour: number
  end_hour: number
  days: {
    mon: boolean
    tue: boolean
    wed: boolean
    thu: boolean
    fri: boolean
    sat: boolean
    sun: boolean
  }
  cpm: number
  ratecard: number
  /** Source of truth entered by the station: 'cpm' | 'ratecard' */
  pricing_type?: "cpm" | "ratecard"
  created_at?: string
  updated_at?: string
}

export interface StationRatesResponse {
  status: string
  rates: StationRate[] | null
  total?: number
  limit?: number
  offset?: number
  /** get_stationrates returns the count here (page-based paging). */
  pagination?: { total: number; page?: number; limit?: number } | null
  data: {
    station: {
      id: number
      call_sign: string
      name: string
      dial_position: number
      format: string
    }
  }
  environment: string
}

export interface CreateStationRateRequest {
  station_id: number
  spot_length: 15 | 30 | 60
  start_hour: number
  end_hour: number
  days: {
    mon: boolean
    tue: boolean
    wed: boolean
    thu: boolean
    fri: boolean
    sat: boolean
    sun: boolean
  }
  cpm?: number
  ratecard?: number
  /** Source of truth entered by the station: 'cpm' | 'ratecard' */
  pricing_type?: "cpm" | "ratecard"
  friendly_name?: string
  auto_generate_all_lengths?: boolean
}

export interface UpdateStationRateRequest {
  friendly_name?: string
  spot_length?: 15 | 30 | 60
  start_hour?: number
  end_hour?: number
  days?: {
    mon: boolean
    tue: boolean
    wed: boolean
    thu: boolean
    fri: boolean
    sat: boolean
    sun: boolean
  }
  cpm?: number
  ratecard?: number
  /** Source of truth entered by the station: 'cpm' | 'ratecard' */
  pricing_type?: "cpm" | "ratecard"
}

export interface BulkCreateStationRatesRequest {
  rates: CreateStationRateRequest[]
}

export interface CreateStationRateResponse {
  status: string
  message: string
  created_entries: Array<{
    id: number
    friendly_name: string
    spot_length: number
    cpm: number
    ratecard: number
  }>
  environment: string
}

export interface BulkDeleteStationRatesResponse {
  status?: string
  message?: string
  deleted_count: number
  skipped_count: number
  deleted_ids?: number[]
  skipped_ids?: number[]
}

// export interface Station {
//   id: number
//   call_sign: string
//   dial_position: number
//   name: string
//   format: string
//   logo?: string
//   inverted_logo?: string
//   coverage_area?: string
//   coverage_map?: string
//   audience?: string
//   url?: string
//   max_rate?: number
//   max_CPM?: number
//   owner_id: number
//   created_at: string
//   owner_name: string
// }

export interface StationResponse {
  status: string
  stations: Station[]
  pagination: {
    total: number
    limit: number
    offset: number
    has_more: boolean
  }
  environment: string
}

@Injectable({
  providedIn: "root",
})
export class StationRatesService {
  private baseUrl = environment.apiUrl
  private selectedStationSubject = new BehaviorSubject<Station | null>(null)
  public selectedStation$ = this.selectedStationSubject.asObservable()

  constructor(
    private apiService: ApiService,
    private stationStore: StationStoreService,
  ) {}

  // Get all stations (centralized cache)
  getStations(): Observable<StationResponse> {
    return this.stationStore.getStationsListResponse().pipe(
      map((res) => ({
        status: "success",
        stations: res.stations,
        pagination: res.pagination,
        environment: "",
      })),
    )
  }

  // Get station rates by station ID
  getStationRates(stationId: number, limit?: number, offset?: number, audiolength?: number): Observable<StationRatesResponse> {
    let url = `${this.baseUrl}/station-rates?station_id=${stationId}`
    if (limit) {
      // get_stationrates pages by `page` (1-based) and ignores `offset`.
      url += `&limit=${limit}&page=${Math.floor((offset || 0) / limit) + 1}`
    }
    if (audiolength) url += `&audiolength=${audiolength}`
    return this.apiService.get<StationRatesResponse>(url)
  }

  // Get specific station rate by ID
  getStationRateById(rateId: number): Observable<{ status: string; rate: StationRate; environment: string }> {
    return this.apiService.get<{ status: string; rate: StationRate; environment: string }>(
      `${this.baseUrl}/station-rates/${rateId}`,
    )
  }

  // Create new station rate
  createStationRate(rateData: CreateStationRateRequest): Observable<CreateStationRateResponse> {
    return this.apiService.post<CreateStationRateResponse>(`${this.baseUrl}/station-rates`, rateData)
  }

  // Create multiple station rates
  createBulkStationRates(bulkData: BulkCreateStationRatesRequest): Observable<CreateStationRateResponse> {
    return this.apiService.post<CreateStationRateResponse>(`${this.baseUrl}/station-rates/bulk`, bulkData)
  }

  // Update existing station rate
  updateStationRate(
    rateId: number,
    updateData: UpdateStationRateRequest,
  ): Observable<{ status: string; message: string; updated_rate: StationRate; environment: string }> {
    return this.apiService.put<{ status: string; message: string; updated_rate: StationRate; environment: string }>(
      `${this.baseUrl}/station-rates/${rateId}`,
      updateData,
    )
  }

  // Delete station rate
  deleteStationRate(
    rateId: number,
  ): Observable<{ status: string; message: string; deleted_id: number; environment: string }> {
    return this.apiService.delete<{ status: string; message: string; deleted_id: number; environment: string }>(
      `${this.baseUrl}/station-rates/${rateId}`,
    )
  }

  bulkDeleteRates(rateIds: number[]): Observable<BulkDeleteStationRatesResponse> {
    return this.apiService.authenticatedDelete<BulkDeleteStationRatesResponse>(
      `${environment.apiUrl}/stationRates/bulk`,
      { body: { rate_ids: rateIds } },
    )
  }

  // Set selected station
  setSelectedStation(station: Station | null): void {
    this.selectedStationSubject.next(station)
  }

  // Get selected station
  getSelectedStation(): Station | null {
    return this.selectedStationSubject.value
  }

  // Helper method to format time display
  formatTimeRange(startHour: number, endHour: number): string {
    const formatHour = (hour: number): string => {
      if (hour === 0) return "12a"
      if (hour < 12) return `${hour}a`
      if (hour === 12) return "12p"
      return `${hour - 12}p`
    }

    const displayEndHour = endHour + 1 === 24 ? 0 : endHour + 1
    return `${formatHour(startHour)}-${formatHour(displayEndHour)}`
  }

  // Helper method to format days
  formatDays(days: StationRate["days"]): string {
    if (!days) return ""
    const selected = [days.mon, days.tue, days.wed, days.thu, days.fri, days.sat, days.sun].filter(Boolean).length
    if (selected === 7) {
      return "Daily"
    }
    // Contiguous runs as ranges, separate runs with ", " (Mon-Fri and Sat-Sun fall out naturally).
    return formatRateDayRuns(days)
  }

  // Helper method to format currency
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  // Helper method to format CPM
  formatCPM(cpm: number): string {
    return `$${cpm.toFixed(2)}`
  }
}
